import { Avatar, Button } from "@mui/material";
import { AppImages } from "app/appImages";
import { IconButtonDelete } from "components/iconButtons";
import { MprcSyncIndicator } from "components/MprcSyncIndicator";
import { MprcText } from "components/MprcText";
import { useMprcTheme } from "components/MprcThemeProvider";
import { AsyncUploadResult } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import * as React from "react";
import { useEffect, useState } from "react";
import { Accept } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { FileToUpload, ImageSelectionBackgroundType, ImageSelectionResult } from ".";
import { AsyncUploader } from "../AsyncUploader";
import { MprcImageSelectionDialog } from "./MprcImageSelectionDialog";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IUploadProps {
  children?: React.ReactNode;

  acceptedFiles?: Accept;
  allowEdit: boolean;
  allowSelect?: boolean;
  boxDimensions?: ImageDimension;
  backgroundType: ImageSelectionBackgroundType;
  imageDimensions?: ImageDimension;
  adjustImageDimensionToRatio?: boolean;
  enableClear?: boolean;
  selectButtomText?: string;
  maxAcceptedHeight?: number;
  maxAcceptedWidth?: number;

  variant?: 'circular' | 'rounded' | 'square';
  initialSource?: "library" | "local";

  currentImageUrl?: string | null;
  emptyImageUrl?: string;
  searchTerm?: string;

  onClear?: () => void;

  /**
   * Ao iniciar o Uplaod.
   * @returns
   */
  onFileUploadStarted?: () => void;

  /**
   * Ao terminaar o Upload.
   * @returns
   */
  onFileUploadFinished?: () => void;

  /**
   * Progresso do Upload.
   * @param progress
   * @returns
   */
  onFileUploadProgress?: (progress: number) => void;
  /**
   * Ao terminar o upload.
   * @param data
   * @returns True, se o upload for aceito; False, se deve recusar o upload.
   */
  onFileUploadSuccess?: (data: AsyncUploadResult) => boolean | undefined;

  /**
   * Ao ocorrer um erro no upload.
   * @param error
   * @returns
   */
  onFileUploadError?: (error: any) => void;
}

/////////////////////////////////////////////////////////////////////////////////////////////////

const RenderBox: React.FC<{ children?: React.ReactNode; boxHeight?: number; boxWidth?: number; }> = (props) => {
  return (
    <div
      style={
        {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          overflow: "hidden",
          backgroundColor: "#e5e5e5",
          border: "solid 1px #808080",
          padding: "8px",
          height: props.boxHeight || "auto",
          width: props.boxWidth || "auto",
        }
      }
    >
      {props.children}
    </div>
  );
};

/////////////////////////////////////////////////////////////////////////////////////////////////

const RenderImage: React.FC<{ url: string | null, width?: number, height?: number; variant: "square" | "circular" | "rounded" | undefined; }> = (props) => {
  return (
    <Avatar
      variant={props.variant}
      src={props.url == null || props.url == "" ? AppImages.TakePicture : props.url}
      sx={(theme) => ({
        margin: "0 auto",
        height: props.height || "inherit",
        width: props.width || "inherit",
        backgroundColor: (theme) => theme.palette.grey[100],
        "& img": {
          objectFit: "contain"
        }
      })}

    />
  );
};

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IDisplayImageProps {
  url: string | null;
  imageDimensions?: ImageDimension;
  isEmpty: boolean;
  uploading: boolean;
  enableClear?: boolean;
  boxDimensions?: ImageDimension;
  variant?: 'circular' | 'rounded' | 'square';
  onClear?: () => void;
}

const DisplayImage: React.FC<IDisplayImageProps> = (props) => {

  const [mouseOver, setMouseOver] = useState<boolean>(false);

  return (
    <div style={{ position: "relative" }}
      onMouseEnter={() => { setMouseOver(true); }}
      onMouseLeave={() => { setMouseOver(false); }}
    >
      {
        props.enableClear && !props.isEmpty && mouseOver &&
        <div style={{ position: "absolute", right: 0, zIndex: 10 }}>
          <IconButtonDelete color="error" size="small" onClick={props.onClear} />
        </div>
      }

      {
        props.uploading &&
        <div style={{ position: "absolute", right: 0, zIndex: 10 }}>
          <MprcText color="info"><MprcSyncIndicator /></MprcText>
        </div>
      }

      {
        props.boxDimensions !== undefined
          ? <RenderBox boxHeight={props.boxDimensions.height} boxWidth={props.boxDimensions.width}>
            <RenderImage url={props.url} variant={props.variant} />
          </RenderBox>
          : <RenderImage url={props.url} width={props.imageDimensions?.width} height={props.imageDimensions?.height} variant={props.variant} />
      }
    </div>
  );
};

/////////////////////////////////////////////////////////////////////////////////////////////////

export const MprcImageSelection: React.FC<IUploadProps> = (props) => {
  const { t } = useTranslation("shared");
  const { theme } = useMprcTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [dialog, setDialog] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [lastUpload, setLastUpload] = useState<AsyncUploadResult | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [finalImage, setFinalImage] = useState<FileToUpload | null>(null);

  const {
    acceptedFiles = { "image/*": [] },
    allowEdit = false,
    allowSelect = true,
    initialSource = "library",
    variant = "square",
    backgroundType = "solid",
    adjustImageDimensionToRatio,
    boxDimensions,
    currentImageUrl,
    emptyImageUrl,
    enableClear,
    imageDimensions,
    selectButtomText,
    onClear,
    onFileUploadError,
    onFileUploadFinished,
    onFileUploadProgress,
    onFileUploadStarted,
    onFileUploadSuccess
  } = props;

  //
  const handleClear = () => {
    setLastUpload(null);
    setPreviewUrl(null);
    setFinalImage(null);
    onClear && onClear();
  };

  //
  const getImageUrl = React.useMemo(() => {
    return previewUrl
      ? previewUrl
      : currentImageUrl && currentImageUrl.length > 0
        ? currentImageUrl
        : emptyImageUrl ?? null;
  }, [previewUrl, currentImageUrl, emptyImageUrl]);

  //
  const doOnFileUploadSuccess = (data: AsyncUploadResult) => {
    const oldUploadCompleted = lastUpload;

    setLastUpload(data);

    setPreviewUrl(data.thumbnail?.url ?? data.mainFile?.url ?? emptyImageUrl ?? null);

    if (onFileUploadSuccess) {
      if (onFileUploadSuccess(data) === false) {
        // Se o Upload não for aceito, reseta o preview.
        setPreviewUrl(emptyImageUrl ?? null);
      }
    }

    if (oldUploadCompleted) {
      var files = oldUploadCompleted.mainFile ? [oldUploadCompleted.mainFile.fileId] : [];
      if (oldUploadCompleted.thumbnail != null) files.push(oldUploadCompleted.thumbnail.fileId);
      asyncUploader.delete(files);
    }
  };

  //
  const handleChooseImage = (image: ImageSelectionResult | null) => {

    if (image != null) {
      var finalImage = (allowEdit === true) ? image.editedImage : image.source;
      if (finalImage != null) {
        setFinalImage(finalImage);
      }
    }
    setDialog(false);
  };

  ////////////////////////////////////////////////////////////

  useEffect(() => {
    if (finalImage) {
      asyncUploader.uploadImage(finalImage);
    }
  }, [finalImage]);

  useEffect(() => {
    setPreviewUrl(null);
  }, [currentImageUrl]);

  ////////////////////////////////////////////////////////////

  const url = getImageUrl;

  const asyncUploader: AsyncUploader = new
    AsyncUploader({
      onFileUploadSuccess: doOnFileUploadSuccess,
      onFileStarted: () => { setUploading(true); onFileUploadStarted && onFileUploadStarted(); },
      onFileFinished: () => { setUploading(false); onFileUploadFinished && onFileUploadFinished(); },
      onFileUploadProgress: onFileUploadProgress,
      onFileUploadError: (instance, error) => {
        if (instance.isCancel(error)) {
          enqueueSnackbar(t("AsyncUpload.UploadCanceled", { ns: "shared" }), { variant: "info" });
        }
        else if (error.storageExceeded) {
          enqueueSnackbar(t("AsyncUpload.StorageExceededMessage"), { variant: "error" });
        }
        else {
          enqueueSnackbar(error.message, { variant: "error" });
        }
        if (onFileUploadError) onFileUploadError(error);
      }
    });

  return (
    <>
      {
        dialog &&
        <MprcImageSelectionDialog
          initialSearch={props.searchTerm}
          acceptedFiles={acceptedFiles}
          initialSource={initialSource!}
          allowEdit={allowEdit}
          dimension={imageDimensions}
          backgroundType={backgroundType}
          adjustImageDimensionToRatio={adjustImageDimensionToRatio}
          onClose={handleChooseImage}
        />
      }

      <div style={{ textAlign: "center" }}>

        <DisplayImage
          url={url}
          isEmpty={url == undefined || url == null || url == emptyImageUrl}
          uploading={uploading}
          imageDimensions={imageDimensions}
          boxDimensions={boxDimensions}
          enableClear={enableClear}
          onClear={handleClear}
          variant={variant}
        />
        {allowSelect && <>
          <br />
          <Button
            color="info"
            disabled={uploading}
            onClick={() => setDialog(true)}
            variant="contained"
            fullWidth
          >
            {selectButtomText || theme.MprcImageSelection.selectImageText}
          </Button>
        </>}
      </div>
    </>
  );
};
